import { ReactElement, ReactNode, forwardRef } from 'react';
import { Platform, Pressable, PressableProps, View } from 'react-native';
import { TIconProps } from '@/common/components/Icons/Icon';
import { IconLoader } from '@/common/components/Icons/custom/IconLoader';
import { Label } from '@/common/components/Label/Label';
import { LayoutConformance } from '@/common/components/LayoutConformance/LayoutConformance';
import { Stack } from '@/common/components/Stack/Stack';
import { IDataAttributes } from '@coverflex-tech/hypernova-utils';
import cx from 'classnames';
import { openURL } from 'expo-linking';
import { styles } from './Button.styles';

export interface IButtonProps
  extends IDataAttributes,
    Omit<PressableProps, 'onClick'> {
  as?: React.ElementType;
  type?: 'button' | 'submit' | 'reset';
  variant?: 'primary' | 'secondary' | 'tertiary' | 'info';
  size?: 'md' | 'sm';
  disabled?: boolean;
  presentational?: boolean;
  loading?: boolean;
  className?: string;
  icon?: ReactElement<TIconProps>;
  iconAlign?: 'left' | 'right';
  onPress?: () => void;
  children?: ReactNode;
}

export const Button = forwardRef(function Btn(
  {
    as = 'button',
    type = 'button',
    variant = 'primary',
    size = 'md',
    disabled,
    presentational = false,
    loading,
    className,
    icon,
    iconAlign = 'left',
    onPress,
    children,
    ...dataProps
  }: IButtonProps,
  ref
) {
  const isDisabled = disabled || loading;
  const parent = (() => {
    if (presentational && Platform.OS === 'web') return <View />;

    if (Platform.OS === 'web') {
      const Component = as;
      const { hrefAttrs, ...rest } = dataProps;
      return (
        <Component {...hrefAttrs} {...rest} type={type} onClick={onPress} />
      );
    }

    return (
      <Pressable
        onPress={() => {
          onPress?.();
          if (dataProps.href) {
            openURL(dataProps.href);
          }
        }}
        {...dataProps}
      />
    );
  })();

  const color = (() => {
    if (isDisabled) {
      return 'neutral20';
    }
    switch (variant) {
      case 'primary':
      case 'secondary':
        return 'neutral30';
      case 'tertiary':
        return 'accentPrimary10';
      default:
        return 'neutral30';
    }
  })();

  const IconComponent = icon ? (
    <icon.type color={color} size={size === 'sm' ? 14 : 16} {...icon.props} />
  ) : null;

  return (
    <LayoutConformance mode="strict">
      <parent.type
        className={styles.root({
          variant,
          size,
          disabled: isDisabled,
          empty: !children,
          className,
        })}
        disabled={isDisabled}
        {...parent.props}
        ref={ref}
      >
        {loading && <IconLoader className={styles.loadingIcon} color="gray" />}
        <Stack
          align="center"
          justify="center"
          gap="sm"
          horizontal
          className={cx({ [styles.loadingContent]: loading })}
        >
          {iconAlign === 'left' && IconComponent}
          {children && (
            <Label className={styles.label({ variant, disabled: isDisabled })}>
              {children}
            </Label>
          )}
          {iconAlign === 'right' && IconComponent}
        </Stack>
      </parent.type>
    </LayoutConformance>
  );
});
